import React from "react";
import * as ReactDOM from "react-dom";
import { Icon, CompanyLogo } from "./registry";
import { localYmd } from "./lib/dates";
import { payrollOf, recentPeriods, hoursSheet, hoursCsvRows, toCsv, workersOf, WEEKDAYS, OT_WEEKLY } from "./lib/hours";
/* ============================================================
   Fence Works — Payroll hours (Reports)
   The crew's clocked hours for one pay period, per worker: every entry with
   its clock in/out, breaks, the GPS check at each end, and any office
   override; daily and weekly totals; overtime past 40 a workweek flagged.
   CSV for the payroll service, and a clean print view to sign. The math is
   lib/hours.ts (tested in scripts/unit-hours.mjs); this is the card.
   ============================================================ */

const PERIOD_KINDS: [string, string][] = [["weekly", "Weekly"], ["biweekly", "Every 2 weeks"], ["semimonthly", "Twice a month"]];
const clock = (t: any) => (t ? new Date(t).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "—");
const dayName = (ymd: string) => new Date(ymd + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
const hrs = (n: number) => (Math.round((Number(n) || 0) * 100) / 100).toString();
const gpsText = (g: any) => !g ? "—" : g.where === "inside" ? "on site" + (g.distMi != null ? " · " + g.distMi + " mi" : "")
  : g.where === "outside" ? g.distMi + " mi away" : g.where;
const slug = (s: string) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

function download(name: string, csv: string) {
  const url = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a"); a.href = url; a.download = name; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function HoursExportCard({ store }: any) {
  const settings = (store && store.settings) || {};
  const crews = settings.crews || [];
  const payroll = payrollOf(settings);
  const today = localYmd();
  const periods = React.useMemo(() => recentPeriods(today, payroll, 10), [today, payroll.period, payroll.weekStart, payroll.anchor]);
  const [pi, setPi] = React.useState<any>(0);
  const [worker, setWorker] = React.useState<any>("");
  const [printing, setPrinting] = React.useState<any>(false);
  const [done, setDone] = React.useState<any>("");
  const period = periods[Math.min(pi, periods.length - 1)];
  const sheet = React.useMemo(() => hoursSheet({ workOrders: store.workOrders || [], crews, period, weekStart: payroll.weekStart, worker: worker || null }),
    [store.workOrders, crews, period, payroll.weekStart, worker]);
  const everyone = React.useMemo(() => {
    const names = new Set<string>();
    crews.forEach((c: any) => workersOf(c).forEach((n) => names.add(n)));
    hoursSheet({ workOrders: store.workOrders || [], crews, period, weekStart: payroll.weekStart }).workers.forEach((w: any) => names.add(w.worker));
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [store.workOrders, crews, period, payroll.weekStart]);

  function setPayroll(patch: any) {
    const next = { ...payroll, ...patch };
    // a biweekly count needs a date a pay period started on — default to this week's start
    if (next.period === "biweekly" && !next.anchor) next.anchor = recentPeriods(today, { ...next, period: "weekly" }, 1)[0].start;
    store.setSettings({ payroll: next });
    setPi(0); setDone("");
  }
  function exportCsv() {
    const rows = hoursCsvRows(sheet);
    download(`fenceworks-hours-${period.start}_${period.end}${worker ? "-" + slug(worker) : ""}.csv`, toCsv(rows));
    setDone(`Hours CSV — ${sheet.rows.length} entr${sheet.rows.length === 1 ? "y" : "ies"}`);
  }
  const totals = sheet.workers.reduce((a: any, w: any) => ({ hours: a.hours + w.hours, ot: a.ot + w.otHours }), { hours: 0, ot: 0 });

  return (
    <div className="card pad" style={{ marginTop: 14 }} data-hours-export>
      <div className="row between" style={{ marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div className="kicker"><Icon name="clock" size={13} /> Payroll hours</div>
        <div className="muted" style={{ fontSize: 12 }}>Clocked crew hours per worker, per pay period — CSV or a sheet to sign</div>
      </div>

      <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "flex-end", marginBottom: 12 }}>
        <div>
          <label className="field-label" htmlFor="hx-kind">Pay period</label>
          <select id="hx-kind" className="select" value={payroll.period} onChange={(e) => setPayroll({ period: e.target.value })} style={{ width: 170 }}>
            {PERIOD_KINDS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </select>
        </div>
        {payroll.period === "biweekly"
          ? <div>
              <label className="field-label" htmlFor="hx-anchor">A pay period started on</label>
              <input id="hx-anchor" className="input" type="date" value={payroll.anchor || ""} onChange={(e) => e.target.value && setPayroll({ anchor: e.target.value })} style={{ width: 160 }} />
            </div>
          : <div>
              <label className="field-label" htmlFor="hx-week">{payroll.period === "semimonthly" ? "Workweek starts (for overtime)" : "Week starts"}</label>
              <select id="hx-week" className="select" value={payroll.weekStart} onChange={(e) => setPayroll({ weekStart: Number(e.target.value) })} style={{ width: 150 }}>
                {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
              </select>
            </div>}
        {payroll.period === "semimonthly" && <span className="muted" style={{ fontSize: 12, paddingBottom: 10 }}>1st–15th and 16th–end of the month</span>}
      </div>

      <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "flex-end" }}>
        <div>
          <label className="field-label" htmlFor="hx-period">Period</label>
          <select id="hx-period" className="select" value={pi} onChange={(e) => setPi(Number(e.target.value))} style={{ width: 300, maxWidth: "100%" }}>
            {periods.map((p, i) => <option key={p.start} value={i}>{p.label}{i === 0 ? " (current)" : ""}</option>)}
          </select>
        </div>
        <div>
          <label className="field-label" htmlFor="hx-worker">Worker</label>
          <select id="hx-worker" className="select" value={worker} onChange={(e) => setWorker(e.target.value)} style={{ width: 190 }}>
            <option value="">Everyone</option>
            {everyone.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <button className="btn btn-outline btn-sm" onClick={exportCsv} disabled={!sheet.rows.length}><Icon name="download" size={13} />Download CSV</button>
        <button className="btn btn-outline btn-sm" onClick={() => setPrinting(true)} disabled={!sheet.rows.length}><Icon name="printer" size={13} />Print view</button>
        {done && <span className="chip green" style={{ fontSize: 11 }}><Icon name="check" size={11} />{done}</span>}
      </div>

      {sheet.workers.length === 0
        ? <p className="muted" style={{ fontSize: 13, margin: "14px 0 2px" }}>No hours logged {worker ? "for " + worker + " " : ""}in {period.label}. Hours come from Crew Mode clock-ins and the hours logged on each work order.</p>
        : <div style={{ overflowX: "auto", marginTop: 14 }}>
            <table className="hx-table">
              <thead><tr><th>Worker</th><th>Crew</th><th className="num">Days</th><th className="num">Hours</th><th className="num">Regular</th><th className="num">Overtime</th></tr></thead>
              <tbody>
                {sheet.workers.map((w: any) => (
                  <tr key={w.worker}>
                    <td style={{ fontWeight: 600 }}>{w.worker}</td>
                    <td className="muted">{w.crew || "—"}</td>
                    <td className="num">{w.days.length}</td>
                    <td className="num">{hrs(w.hours)}</td>
                    <td className="num">{hrs(w.regular)}</td>
                    <td className="num">{w.otHours > 0 ? <span className="chip" style={{ fontSize: 11, color: "var(--red)", borderColor: "var(--red-tint)" }}>{hrs(w.otHours)} OT</span> : "0"}</td>
                  </tr>
                ))}
              </tbody>
              {sheet.workers.length > 1 && <tfoot><tr><td colSpan={3}>All workers</td><td className="num">{hrs(totals.hours)}</td><td className="num">{hrs(totals.hours - totals.ot)}</td><td className="num">{hrs(totals.ot)}</td></tr></tfoot>}
            </table>
          </div>}
      <p className="faint" style={{ fontSize: 11, margin: "10px 0 0" }}>A crew entry counts for every member on that crew (Settings → Crews). Overtime is time past {OT_WEEKLY} hours in a workweek starting {WEEKDAYS[payroll.weekStart]}; hours earlier in a week that began in the last period count toward the {OT_WEEKLY}. The CSV has every entry with its GPS check at clock in and out and any office override, then day, week and period totals.</p>
      {printing && <HoursPrintSheet sheet={sheet} store={store} onClose={() => setPrinting(false)} />}
    </div>
  );
}

/* the sheet to print and sign — one section per worker (white on screen too;
   window.print() prints only this, via the cs-overlay print rules) */
function HoursPrintSheet({ sheet, store, onClose }: any) {
  const company = (store && store.company) || {};
  const settings = (store && store.settings) || {};
  React.useEffect(() => {
    const f = (e: any) => { if (e.key === "Escape") onClose && onClose(); };
    window.addEventListener("keydown", f);
    return () => window.removeEventListener("keydown", f);
  }, [onClose]);
  return ReactDOM.createPortal(
    <div className="cs-overlay" data-dialog onClick={onClose}>
      <div className="cs-modal hx-modal" onClick={(e: any) => e.stopPropagation()}>
        <div className="cs-bar no-print">
          <span className="row gap-8" style={{ fontSize: 13, fontWeight: 700 }}><Icon name="clock" size={14} className="muted" />Hours sheet · {sheet.period.label}</span>
          <div className="row gap-8">
            <button className="btn btn-primary btn-sm" onClick={() => window.print()}><Icon name="printer" size={14} />Print / PDF</button>
            <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close"><Icon name="x" size={14} /></button>
          </div>
        </div>
        <div className="cs-scroll">
          {sheet.workers.map((w: any, wi: number) => (
            <div key={w.worker} className="crew-sheet hx-sheet" style={{ marginTop: wi ? 18 : 0 }}>
              <div className="cs-head">
                <div className="row gap-12">
                  <CompanyLogo company={company} size={36} />
                  <div>
                    <div className="display" style={{ fontSize: 17 }}>{company.name || settings.companyName || "Your Company"}</div>
                    <div className="cs-dim">Hours sheet · {sheet.period.label}</div>
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div className="display" style={{ fontSize: 17 }}>{w.worker}</div>
                  <div className="cs-dim">{w.crew || "No crew"} · printed {new Date().toLocaleDateString()}</div>
                </div>
              </div>
              <table className="cs-table hx-print">
                <thead><tr><th>Date</th><th>Job</th><th>In</th><th>Out</th><th className="num">Hours</th><th className="num">Breaks</th><th>GPS in</th><th>GPS out</th><th>Override</th></tr></thead>
                <tbody>
                  {w.weeks.map((wk: any) => (
                    <React.Fragment key={wk.start}>
                      {w.days.filter((d: any) => d.date >= wk.start && d.date <= wk.end).map((d: any) => (
                        <React.Fragment key={d.date}>
                          {d.rows.map((r: any, i: number) => (
                            <tr key={r.entryId + i} className={r.otHours > 0 ? "hx-ot" : undefined}>
                              <td className="nw">{i === 0 ? dayName(r.date) : ""}</td>
                              <td className="hx-job">{r.job}{r.note ? <div className="cs-dim">{r.note}</div> : null}</td>
                              <td className="nw">{clock(r.clockIn)}</td>
                              <td className="nw">{clock(r.clockOut)}</td>
                              <td className="num">{hrs(r.hours)}{r.otHours > 0 ? <div className="hx-ot-tag">{hrs(r.otHours)} OT</div> : null}</td>
                              <td className="num nw">{r.breakMins != null ? r.breakMins + "m" : "—"}</td>
                              <td className="nw">{gpsText(r.gpsIn)}</td>
                              <td className="nw">{gpsText(r.gpsOut)}</td>
                              <td>{r.override ? <><b>{r.override.kind}</b><div className="cs-dim">{[r.override.by, r.override.at ? new Date(r.override.at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "", r.override.reason, r.override.from != null ? "was " + hrs(r.override.from) + " hr" : ""].filter(Boolean).join(" · ")}</div></> : "—"}</td>
                            </tr>
                          ))}
                          {d.rows.length > 1 && <tr className="hx-day"><td colSpan={4}>{dayName(d.date)} total</td><td className="num">{hrs(d.total)}</td><td colSpan={4}></td></tr>}
                        </React.Fragment>
                      ))}
                      <tr className={"hx-week" + (wk.overtime ? " hx-ot" : "")}>
                        <td colSpan={4}>Week of {dayName(wk.start)}{wk.before > 0 ? " · " + hrs(wk.before) + " hr earlier that week (last period)" : ""}</td>
                        <td className="num">{hrs(wk.hours)}</td>
                        <td colSpan={4}>{wk.otHours > 0 ? <b>{hrs(wk.regular)} regular · {hrs(wk.otHours)} overtime (over {OT_WEEKLY})</b> : hrs(wk.regular) + " regular"}</td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot><tr><td colSpan={4}><b>Period total</b></td><td className="num"><b>{hrs(w.hours)}</b></td><td colSpan={4}><b>{hrs(w.regular)} regular{w.otHours > 0 ? " · " + hrs(w.otHours) + " overtime" : ""}</b></td></tr></tfoot>
              </table>
              <div className="cs-sign">
                <div className="cs-sign-col"><div className="cs-sign-rule"></div><div className="cs-sign-lbl cs-dim">Employee — these hours are right</div></div>
                <div className="cs-sign-col"><div className="cs-sign-rule"></div><div className="cs-sign-lbl cs-dim">Approved by</div></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>, document.body);
}

Object.assign(window, { HoursExportCard });

export { HoursExportCard, HoursPrintSheet };
